import {
  HEADER_COMPASS_BADGE_THEME_VARS,
  HEADER_COMPASS_THEME_VARS,
  HEADER_ICON_BUTTON_ACTIVE_STATES_CLASS,
} from "@circle-react-shared/uikit/Header/headerThemeVars";
import { useHeaderBorderStyles } from "@circle-react-shared/uikit/HeaderV3/useHeaderColors";
import { cssAnchorProps } from "@circle-react/helpers/cssAnchorHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useIsCustomColorTheme } from "@circle-react/hooks/useIsCustomColorTheme";
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import { t } from "@/i18n-js/instance";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { CenterSlot } from "./CenterSlot";
import { LeftSlot } from "./LeftSlot";
import { RightSlot } from "./RightSlot";

interface HeaderWrapperProps {
  children: ReactNode;
  className?: string;
}

export const HeaderWrapperRoot = ({
  children,
  className,
}: HeaderWrapperProps) => {
  const styles = useHeaderBorderStyles();
  const { isCircleV4 } = useCircleV4();
  // Default theme opts out of community branding entirely,
  // falling back to Compass's own default tokens
  const isCustomColorTheme = useIsCustomColorTheme();

  let leftSlot: ReactElement | undefined;
  let centerSlot: ReactElement | undefined;
  let rightSlot: ReactElement | undefined;

  Children.forEach(children, child => {
    if (!isValidElement(child)) {
      return;
    }

    if (child.type === LeftSlot) {
      leftSlot = child;
    } else if (child.type === CenterSlot) {
      centerSlot = child;
    } else if (child.type === RightSlot) {
      rightSlot = child;
    }
  });

  return (
    <nav
      data-testid="navigation-bar-wrapper"
      aria-label={t("main_navigation_bar")}
      className={classNames(
        // 3 column grid: left and right grow equally from min-content so the
        // nav stays centered; the center column sizes to the links and can shrink
        "sticky top-0 z-50 grid h-[64px] grid-cols-[minmax(min-content,1fr)_minmax(0,auto)_minmax(min-content,1fr)] items-center gap-x-4 border-b border-primary bg-c-header pl-6 pr-4",
        {
          "dark:border-secondary": isCircleV4,
        },
        HEADER_ICON_BUTTON_ACTIVE_STATES_CLASS,
        isCustomColorTheme && HEADER_ICON_BUTTON_ACTIVE_STATES_CLASS,
        className,
      )}
      style={{
        ...(isCustomColorTheme
          ? HEADER_COMPASS_THEME_VARS
          : HEADER_COMPASS_BADGE_THEME_VARS),
        ...styles,
      }}
      {...cssAnchorProps({ anchor: "app-topbar" })}
    >
      {leftSlot}
      {centerSlot ?? <CenterSlot />}
      {rightSlot}
    </nav>
  );
};

HeaderWrapperRoot.displayName = "HeaderWrapper";
